import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import './HousekeepingTab.css';

const SERVICE_DEPARTMENT_MAP = {
  room_service: 'kitchen',
  dining: 'kitchen',
  housekeeping: 'housekeeping',
  laundry: 'housekeeping',
  spa: 'front_desk',
  concierge: 'front_desk',
  maintenance: 'maintenance',
};

const formatDepartmentLabel = (department) =>
  String(department || 'front_desk')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const HousekeepingTab = ({ hotelId, token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffOptionsByRequest, setStaffOptionsByRequest] = useState({});
  const [selectedStaffByRequest, setSelectedStaffByRequest] = useState({});
  const [activeActionRequestId, setActiveActionRequestId] = useState(null);

  const categories = [
    { key: 'room_service', label: 'Room Service' },
    { key: 'housekeeping', label: 'Housekeeping' },
    { key: 'dining', label: 'Dining' },
    { key: 'laundry', label: 'Laundry' },
    { key: 'spa', label: 'Spa & Wellness' },
    { key: 'concierge', label: 'Concierge' },
  ];

  useEffect(() => {
    if (hotelId && token) {
      fetchServiceRequests();
    }
  }, [hotelId, token]);

  const fetchServiceRequests = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/services/manager/requests?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(response.data?.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching service requests:', err);
      setRequests([]);
      setLoading(false);
    }
  };

  const getDepartmentForRequest = (request) => {
    const category = String(request.service_type || '').toLowerCase();
    return SERVICE_DEPARTMENT_MAP[category] || 'front_desk';
  };

  const loadStaffOptions = async (requestId) => {
    try {
      setActiveActionRequestId(requestId);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/services/manager/requests/${requestId}/staff-options`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const staff = response.data?.data?.staff || [];
      setStaffOptionsByRequest((prev) => ({ ...prev, [requestId]: staff }));
      if (staff.length > 0) {
        setSelectedStaffByRequest((prev) => ({
          ...prev,
          [requestId]: prev[requestId] || String(staff[0].assignment_id),
        }));
      }
    } catch (err) {
      console.error('Error fetching assignable staff:', err);
      alert(err.response?.data?.message || 'Failed to fetch staff options');
    } finally {
      setActiveActionRequestId(null);
    }
  };

  const assignRequest = async (requestId) => {
    const assignmentId = Number(selectedStaffByRequest[requestId]);
    if (!assignmentId) {
      alert('Please select a staff member');
      return;
    }

    try {
      setActiveActionRequestId(requestId);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/services/manager/requests/${requestId}/assign`,
        { assignmentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchServiceRequests();
    } catch (err) {
      console.error('Error assigning service request:', err);
      alert(err.response?.data?.message || 'Failed to assign request');
    } finally {
      setActiveActionRequestId(null);
    }
  };

  const autoAssignRequest = async (requestId) => {
    try {
      setActiveActionRequestId(requestId);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/services/manager/requests/${requestId}/auto-assign`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchServiceRequests();
    } catch (err) {
      console.error('Error auto-assigning service request:', err);
      alert(err.response?.data?.message || 'Failed to auto-assign request');
    } finally {
      setActiveActionRequestId(null);
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    try {
      setActiveActionRequestId(requestId);
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/services/manager/requests/${requestId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchServiceRequests();
    } catch (err) {
      console.error('Error updating request status:', err);
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActiveActionRequestId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      assigned: '#3b82f6',
      in_progress: '#8b5cf6',
      completed: '#10b981',
      cancelled: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  const groupedByCategory = categories.map((category) => ({
    ...category,
    items: requests.filter(
      (request) => String(request.service_type || '').toLowerCase() === category.key
    ),
  }));

  const stats = {
    total: requests.length,
    pending: requests.filter((request) => request.status === 'pending').length,
    inProgress: requests.filter((request) => ['assigned', 'in_progress'].includes(request.status)).length,
    completed: requests.filter((request) => request.status === 'completed').length,
  };

  if (loading) {
    return <div className="tab-loading"><div className="loading-spinner"></div><p>Loading service requests...</p></div>;
  }

  return (
    <div className="housekeeping-tab">
      <div className="tab-header">
        <h2><Sparkles size={24} /> Services Management</h2>
      </div>

      <div className="stats-grid">
        <div className="stat-box"><span className="stat-value">{stats.total}</span><span className="stat-label">Total Requests</span></div>
        <div className="stat-box"><span className="stat-value">{stats.pending}</span><span className="stat-label">Pending</span></div>
        <div className="stat-box"><span className="stat-value">{stats.inProgress}</span><span className="stat-label">In Progress</span></div>
        <div className="stat-box"><span className="stat-value">{stats.completed}</span><span className="stat-label">Completed</span></div>
      </div>

      {groupedByCategory.map((category) => (
        <div key={category.key} style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px', color: '#111827' }}>
            {category.label} ({category.items.length})
          </h3>

          {category.items.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 16px' }}>
              <p>No requests in this category.</p>
            </div>
          ) : (
            <div className="tasks-list">
              {category.items.map((request) => (
                <div key={request.request_id} className="task-card">
                  {(() => {
                    const isPending = request.status === 'pending';
                    const isAssigned = request.status === 'assigned';
                    const isInProgress = request.status === 'in_progress';
                    const isClosed = ['completed', 'cancelled'].includes(request.status);

                    return (
                      <>
                  <div className="task-header">
                    <span className="room-number">Room {request.room_number || request.room_id}</span>
                    <span className="task-status" style={{ background: getStatusColor(request.status) }}>
                      {request.status}
                    </span>
                  </div>

                  <div className="task-body">
                    <p><strong>Department:</strong> {formatDepartmentLabel(getDepartmentForRequest(request))}</p>
                    <p><strong>Guest:</strong> {request.guest_name || request.guest_email || `User ${request.user_id}`}</p>
                    <p><strong>Priority:</strong> {request.priority || 'normal'}</p>
                    {request.scheduled_time && <p><strong>Scheduled:</strong> {new Date(request.scheduled_time).toLocaleString()}</p>}
                    {request.description && <p><strong>Request:</strong> {request.description}</p>}
                    <p><strong>Created:</strong> {new Date(request.created_at).toLocaleString()}</p>
                    {request.assigned_staff_name && <p><strong>Assigned Staff:</strong> {request.assigned_staff_name}</p>}

                    <div style={{ marginTop: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>Timeline</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                        Created: {request.created_at ? new Date(request.created_at).toLocaleString() : '-'}
                      </p>
                      {request.assigned_at && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                          Assigned: {new Date(request.assigned_at).toLocaleString()}
                        </p>
                      )}
                      {request.completed_at && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                          Completed: {new Date(request.completed_at).toLocaleString()}
                        </p>
                      )}
                      {request.status === 'cancelled' && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#b91c1c', fontWeight: 600 }}>
                          Cancelled: {request.updated_at ? new Date(request.updated_at).toLocaleString() : '-'}
                        </p>
                      )}
                    </div>

                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {isPending && (
                        <>
                          <button
                            type="button"
                            onClick={() => loadStaffOptions(request.request_id)}
                            disabled={activeActionRequestId === request.request_id}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                          >
                            Assign
                          </button>
                          <button
                            type="button"
                            onClick={() => autoAssignRequest(request.request_id)}
                            disabled={activeActionRequestId === request.request_id}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                          >
                            Auto Assign
                          </button>
                        </>
                      )}

                      {isAssigned && (
                        <button
                          type="button"
                          onClick={() => updateRequestStatus(request.request_id, 'in_progress')}
                          disabled={activeActionRequestId === request.request_id}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                        >
                          Mark In Progress
                        </button>
                      )}

                      {isInProgress && (
                        <button
                          type="button"
                          onClick={() => updateRequestStatus(request.request_id, 'completed')}
                          disabled={activeActionRequestId === request.request_id}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                        >
                          Mark Completed
                        </button>
                      )}

                      {isClosed && (
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          Assignment controls are locked for {request.status} requests.
                        </span>
                      )}
                    </div>

                    {isPending && Array.isArray(staffOptionsByRequest[request.request_id]) && staffOptionsByRequest[request.request_id].length > 0 && (
                      <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                          value={selectedStaffByRequest[request.request_id] || ''}
                          onChange={(event) =>
                            setSelectedStaffByRequest((prev) => ({
                              ...prev,
                              [request.request_id]: event.target.value,
                            }))
                          }
                          style={{ minWidth: '260px', padding: '6px' }}
                        >
                          {staffOptionsByRequest[request.request_id].map((staff) => (
                            <option key={staff.assignment_id} value={staff.assignment_id}>
                              {staff.full_name || staff.email} ({formatDepartmentLabel(staff.department)}) - Active tasks: {staff.active_task_count}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => assignRequest(request.request_id)}
                          disabled={activeActionRequestId === request.request_id}
                          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}
                        >
                          Confirm Assign
                        </button>
                      </div>
                    )}
                  </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {requests.length === 0 && <div className="empty-state"><Sparkles size={48} /><h3>No Service Requests</h3><p>No guest service requests yet.</p></div>}
    </div>
  );
};

export default HousekeepingTab;
